import axios from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  CreateUserInput,
  DeletedUserResult,
  UpdateUserInput,
  User,
  UserIdInput,
} from './users.types'

export interface UserOrganizationScope {
  organizationId: OrganizationId
}

/** Scope for calls that act on one user. */
export interface UserScope extends UserOrganizationScope {
  userId?: UserIdInput
}

/** Query functions read only the key. */
type UserQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'>

/** `UserController::index()` reads only the organization, so the user list takes no filters. */
export async function getUsers({
  queryKey: [{ organizationId }],
}: UserQueryContext<UserOrganizationScope>) {
  return (await axios.get<User[]>('/users', buildAdminRequestConfig({ organizationId }))) || []
}

export async function getUser({
  queryKey: [{ organizationId, userId }],
}: UserQueryContext<UserScope>) {
  return (
    (await axios.get<User>(`/users/${userId}`, buildAdminRequestConfig({ organizationId }))) || {}
  )
}

export async function createUser({
  organizationId,
  ...values
}: CreateUserInput & UserOrganizationScope) {
  return axios.post<User>('/users', values, buildAdminRequestConfig({ organizationId }))
}

export async function updateUser({
  organizationId,
  userId,
  ...values
}: UpdateUserInput & UserScope) {
  return axios.put<User>(`/users/${userId}`, values, buildAdminRequestConfig({ organizationId }))
}

export async function softDeleteUser({ organizationId, userId }: UserScope) {
  return axios.delete<DeletedUserResult>(
    `/users/${userId}`,
    buildAdminRequestConfig({ organizationId })
  )
}
